import { Box, Container, Grid, Paper, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import PageProps from '../_types/page-props.interface'
import { useGlobalState } from '../context/global'
import { useError } from '../context/error'
import { StyleHelper } from '../components/style-helper'
import WelcomeModal from '../components/modals/welcome.modal'
import { UserService } from '../services/user.service'
import { CarnivoreUser } from '../_types/user.type'
import useDocumentTitle from '../hooks/useDocumentTitle'
import CarnivoreLogo from '../components/imgs/carnivore-logo'
import AlertModal from '../components/modals/alert.modal'
import WebPush from '../components/webpush'
import CommentModal from '../components/modals/comment.modal'
import { AnalyticsActions, AnalyticsCategories, AnalyticsHelper } from '../_helpers/analytics.helper'

import IndexPulse from '../components/dashboard/index-pulse'
import AlertCenter from '../components/dashboard/alert-center'
import RecentLongTermBuysSells from '../components/dashboard/recent-buys-sells'
import HeatersGlance from '../components/dashboard/heaters-glance'
import RecentCalls from '../components/dashboard/recent-calls'
import LastWrap from '../components/dashboard/last-wrap'
import LastStream from '../components/dashboard/last-stream'
import QuickLinksRow from '../components/dashboard/quick-links-row'

export default function Dashboard(page: PageProps) {
  const { user, setUser, banner } = useGlobalState()
  const { setError } = useError()
  const userService = new UserService(setError)

  const [showWelcome, setShowWelcome] = useState(false)
  const [showCommentBox, setShowCommentBox] = useState(false)
  const [showCommentModal, setShowCommentModal] = useState(false)

  useDocumentTitle(page.title)

  useEffect(() => {
    if (user && !user?.isOnboarded) {
      setShowWelcome(true)
    }
    try {
      if (!window.localStorage.getItem('comment-ask')) {
        const now = new Date()
        window.localStorage.setItem('comment-ask', now.toISOString())
      } else {
        const lastAskStr = window.localStorage.getItem('comment-ask')
        const lastAsk = new Date(lastAskStr ?? '')
        const now = new Date()
        const differenceInDays = (now.getTime() - lastAsk.getTime()) / (1000 * 60 * 60 * 24)
        if (differenceInDays > 14) {
          window.localStorage.setItem('comment-ask', now.toISOString())
          AnalyticsHelper.logEvent(AnalyticsActions.modal_open, AnalyticsCategories.dashboard, 'comment_box')
        }
      }
    } catch (e) { /* ignore */ }
  }, [user])

  const _finishWelcome = () => {
    const tempUser: CarnivoreUser = JSON.parse(JSON.stringify(user))
    tempUser.isOnboarded = true
    userService.updateUser({ isOnboarded: true }).then(res => {
      setUser(res.user)
    })
    setShowWelcome(false)
  }

  return (
    <Container sx={{ pb: 4 }}>
      <Typography variant='h5' gutterBottom>Dashboard</Typography>

      {banner && (
        <Paper elevation={1} sx={{ mb: 2 }}>
          <Box p={3}>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={4} lg={1}>
                <Box width={50}>
                  <CarnivoreLogo />
                </Box>
              </Grid>
              <Grid item xs={8} lg={10}>
                <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                  Important Message
                </Typography>
                <Typography variant='subtitle1' fontWeight='bold'>{banner?.title}</Typography>
                <Typography variant='body1'>{banner?.msg}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      <IndexPulse />

      <StyleHelper.VerticalSpace />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <AlertCenter />
        </Grid>
        <Grid item xs={12} lg={4}>
          <RecentCalls />
        </Grid>
      </Grid>

      <StyleHelper.VerticalSpace />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <HeatersGlance />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box display='flex' flexDirection='column' gap={2}>
            <RecentLongTermBuysSells />
            <LastWrap />
            {user?.isAxxess && <LastStream />}
          </Box>
        </Grid>
      </Grid>

      <StyleHelper.VerticalSpace />

      <QuickLinksRow />

      <StyleHelper.DoubleVerticalSpace />

      <WelcomeModal open={showWelcome} size='lg' onClose={_finishWelcome} />
      <AlertModal
        message=''
        open={showCommentBox}
        size='md'
        customActionText='Leave Feedback'
        header="We'd love to hear your feedback"
        onClose={() => setShowCommentBox(false)}
        customAction={async () => { setShowCommentModal(true) }}
        customContent={
          <Box>
            <Typography variant='body2'>
              How are you liking the new Carnivore experience so far? We'd love to hear your suggestions, comments, or concerns.
            </Typography>
            <StyleHelper.VerticalSpace />
            <Typography variant='body2'>
              Have you also setup your browser notifications yet? Stay up to date with alerts right from your desktop!
            </Typography>
            <StyleHelper.VerticalSpace />
            <WebPush btnDisplay btnText='Manage Browser Alerts' />
          </Box>
        }
      />
      <CommentModal
        size='md'
        open={showCommentModal}
        onClose={() => setShowCommentModal(false)}
      />
    </Container>
  )
}
